import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faXmark, faUser } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import type { User } from '@/models';

interface UserSelectProps {
  selectedUser: User | null;
  onChange: (user: User | null) => void;
  label?: string;
  placeholder?: string;
  id?: string;
}

// Number of users fetched per search query.
const RESULT_LIMIT = 10;

/**
 * Durchsuchbares Auswahlfeld für einen einzelnen Nutzer (oder keinen).
 *
 * Sucht per {@link APIRoutes.listUsers} (debounced) nach Name/E-Mail und gibt das
 * vollständige {@link User}-Objekt an `onChange` zurück, damit der Aufrufer z.B.
 * dessen `referrerCode` verwenden kann. Auswahl kann jederzeit geleert werden.
 */
export default function UserSelect({ selectedUser, onChange, label, placeholder, id }: UserSelectProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounced user search whenever the dropdown is open and the query changes.
  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      const trimmed = query.trim();
      APIRoutes.listUsers({ search: trimmed !== '' ? trimmed : undefined, limit: RESULT_LIMIT })
        .then((response) => {
          if (!cancelled) {
            setResults(response.users);
          }
        })
        .catch((error) => {
          console.error('Error searching users:', error);
          if (!cancelled) {
            setResults([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open]);

  // Close the dropdown when clicking outside of the component.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (user: User) => {
    onChange(user);
    setOpen(false);
    setQuery('');
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
  };

  const userLabel = (user: User): string => {
    const name = `${user.firstName} ${user.lastName}`.trim();
    return name !== '' ? name : user.email;
  };

  return (
    <div ref={containerRef} className="relative">
      {label !== undefined && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}

      {selectedUser !== null ? (
        <div className="flex items-center justify-between gap-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{userLabel(selectedUser)}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{selectedUser.email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer shrink-0"
            title="Auswahl entfernen"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          />
          <input
            id={id}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder ?? 'Nutzer suchen...'}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {open && selectedUser === null && (
        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 italic">Keine Nutzer gefunden</div>
          ) : (
            <ul>
              {results.map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(user)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{userLabel(user)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
